import axios from "axios";
import { API_BASE_URL } from "utils/api";

// const api = axios.create({
//   baseURL: getBaseUrl(),
// });

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // needed if backend uses cookies/sessions
  headers: {
    "ngrok-skip-browser-warning": "true",
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const normalizeCareOf = (item = {}) => ({
  ...item,
  id: item._id || item.id,
  status: (item.status || "").toLowerCase(),
  CareOfType: (item.CareOfType || item.careOfType || "").toLowerCase(),
});

const unwrapRecord = (response) => {
  const payload = response?.data?.data;
  if (payload && typeof payload === "object" && !Array.isArray(payload) && payload.data && !payload._id && !payload.id) {
    return payload.data;
  }
  return payload || {};
};

const buildFormData = (payload = {}) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (key === "profileImage" && value instanceof File) {
      formData.append("profileImage", value);
      return;
    }

    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
      return;
    }

    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, value);
  });

  return formData;
};

const hasFilePayload = (payload = {}) => {
  return Object.values(payload).some((value) => value instanceof File);
};

export const getCareOfList = async ({ page = 1, limit = 10, filters = {} }) => {
  const response = await api.get("/care-of/search", {
    params: {
      page,
      limit,
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.country ? { country: filters.country } : {}),
      ...(filters.state ? { state: filters.state } : {}),
      ...(filters.district ? { district: filters.district } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.source ? { source: filters.source } : {}),
    },
  });

  const payload = response?.data?.data || {};
  const data = Array.isArray(payload.matched?.data) ? payload.matched.data : [];
  const total = payload.pagination?.totalMatched ?? payload.pagination?.totalItems ?? 0;

  return {
    total,
    items: data.map(normalizeCareOf),
  };
};

export const createCareOf = async (payload) => {
  const normalizedPayload = { ...payload };
  if (!(normalizedPayload.profileImage instanceof File)) {
    delete normalizedPayload.profileImage;
  }
  if (Array.isArray(normalizedPayload.documents) && normalizedPayload.documents.length === 0) {
    delete normalizedPayload.documents;
  }

  const useMultipart = hasFilePayload(normalizedPayload);
  const body = useMultipart ? buildFormData(normalizedPayload) : normalizedPayload;
  const response = await api.post("/care-of", body, {
    headers: useMultipart ? { "Content-Type": "multipart/form-data" } : undefined,
  });

  const created = unwrapRecord(response);
  return normalizeCareOf(created);
};

export const getCareOfById = async (id) => {
  const response = await api.get(`/care-of/${encodeURIComponent(id)}`);
  const record = unwrapRecord(response);
  return normalizeCareOf(record);
};

export const updateCareOf = async (id, payload) => {
  const normalizedPayload = { ...payload };
  if (!(normalizedPayload.profileImage instanceof File)) {
    delete normalizedPayload.profileImage;
  }
  if (Array.isArray(normalizedPayload.documents) && normalizedPayload.documents.length === 0) {
    delete normalizedPayload.documents;
  }

  const useMultipart = hasFilePayload(normalizedPayload);
  const body = useMultipart ? buildFormData(normalizedPayload) : normalizedPayload;
  const response = await api.put(`/care-of/${encodeURIComponent(id)}`, body, {
    headers: useMultipart ? { "Content-Type": "multipart/form-data" } : undefined,
  });

  const updated = unwrapRecord(response);
  return normalizeCareOf(updated);
};

export const deleteCareOf = async (id) => {
  await api.delete(`/care-of/${id}`);
};

export const uploadCareOfDocuments = async (careOfId, files) => {
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("documents", file);
  });

  const response = await api.post(`/care-of/${encodeURIComponent(careOfId)}/documents`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return unwrapRecord(response);
};

export const deleteCareOfDocument = async (careOfId, documentId, fileName) => {
  const formData = new FormData();
  formData.append("documentId", documentId);
  formData.append("fileName", fileName);

  const response = await api.request({
    method: "delete",
    url: `/care-of/${encodeURIComponent(careOfId)}/documents`,
    data: formData,
  });

  return unwrapRecord(response);
};

export { normalizeCareOf };
